import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { supabase } from '@/lib/supabaseClient';

interface RoleInfo {
  id: number;
  name: string;
  global: boolean;
  tenant_id: string | null;
}

interface UserInfo {
  role_id: number;
  tenant_id: string;
  role: RoleInfo;
}

interface PermissionContextType {
  permissions: Set<string>;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  userInfo: UserInfo | null;
  roleInfo: RoleInfo | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType>({
  permissions: new Set(),
  hasPermission: () => false,
  hasAnyPermission: () => false,
  hasAllPermissions: () => false,
  userInfo: null,
  roleInfo: null,
  loading: true,
  error: null,
  refetch: async () => {},
});

// Global cache for user permissions to prevent duplicate requests
const permissionCache = new Map<string, {
  permissions: Set<string>;
  userInfo: UserInfo | null;
  roleInfo: RoleInfo | null;
  timestamp: number;
  promise?: Promise<any>;
}>();

// Cache expiration time (15 minutes as per plan)
const CACHE_EXPIRY = 15 * 60 * 1000;

// Expose cache to window for debugging
if (typeof window !== 'undefined') {
  (window as any).__permissionCache = {
    get: () => {
      const entries: any[] = [];
      permissionCache.forEach((value, key) => {
        entries.push({
          key,
          permissions: Array.from(value.permissions),
          roleInfo: value.roleInfo,
          userInfo: value.userInfo,
          age: `${Math.round((Date.now() - value.timestamp) / 1000)}s`,
          expired: Date.now() - value.timestamp >= CACHE_EXPIRY
        });
      });
      return entries;
    },
    clear: () => {
      permissionCache.clear();
    }
  };
}

// Tenant ID will be dynamically retrieved from TenantContext

// Exponential backoff for 522 errors
const retryWithBackoff = async function <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      const isLastAttempt = attempt === maxRetries - 1;
      const is522Error = error?.status === 522 || error?.code === 522;
      
      if (isLastAttempt || !is522Error) {
        throw error;
      }
      
      // Exponential backoff for 522 errors
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  
  throw new Error('Max retries exceeded');
}

// Deduplicated request function that returns permissions AND user/role info
const deduplicatedPermissionRequest = async (
  key: string,
  requestFn: () => Promise<{ permissions: Set<string>; userInfo: UserInfo | null; roleInfo: RoleInfo | null }>
): Promise<{ permissions: Set<string>; userInfo: UserInfo | null; roleInfo: RoleInfo | null }> => {
  const cached = permissionCache.get(key);


  // If we have a valid cached result, return it
  if (cached && Date.now() - cached.timestamp < CACHE_EXPIRY) {
    return {
      permissions: cached.permissions,
      userInfo: cached.userInfo,
      roleInfo: cached.roleInfo
    };
  }

  // If there's an ongoing request, wait for it
  if (cached?.promise) {
    const result = await cached.promise;
    return result;
  }

  // Create new request
  const promise = requestFn();
  permissionCache.set(key, {
    permissions: new Set(),
    userInfo: null,
    roleInfo: null,
    timestamp: Date.now(),
    promise
  });

  try {
    const result = await promise;

    // Only cache non-empty permissions or if we explicitly know this should be empty
    // For superadmin users, we should never cache empty permissions
    if (result.permissions.size > 0) {
      permissionCache.set(key, {
        permissions: result.permissions,
        userInfo: result.userInfo,
        roleInfo: result.roleInfo,
        timestamp: Date.now(),
        promise: undefined
      });
    } else {
      // Don't cache empty permissions - let them be fetched fresh each time
      permissionCache.delete(key);
    }

    return result;
  } catch (error) {
    // Remove failed request from cache
    permissionCache.delete(key);
    throw error;
  }
}

export function PermissionProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { currentTenant, isLoading: tenantLoading } = useTenant();
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [roleInfo, setRoleInfo] = useState<RoleInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isFetchingRef = useRef(false);

  // Memoized cache key to prevent unnecessary re-renders
  const cacheKey = useMemo(() => {
    if (!user?.id || !currentTenant?.tenant_id) return null;
    return `user-permissions-${user.id}-${currentTenant.tenant_id}`;
  }, [user?.id, currentTenant?.tenant_id]);

  const fetchUserPermissions = async () => {
    // Don't fetch if auth or tenant is still loading
    if (authLoading || tenantLoading || !user || !currentTenant || !cacheKey) {
      return;
    }

    // Check what's in the cache
    const cached = permissionCache.get(cacheKey);

    // Clear any potentially stale empty permissions from cache on fresh fetch
    if (cached && cached.permissions.size === 0 && !cached.promise) {
      permissionCache.delete(cacheKey);
    }

    // Prevent duplicate requests
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    setLoading(true);
    setError(null);

    // Abort any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      const result = await deduplicatedPermissionRequest(cacheKey, async () => {
        return await retryWithBackoff(async () => {
          // Get comprehensive user and role information
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select(`
              role_id,
              tenant_id,
              roles!inner (
                id,
                name,
                global,
                tenant_id
              )
            `)
            .eq('id', user.id)
            .single();

          if (userError) {
            throw userError;
          }

          // Prepare user and role information for caching
          let cachedUserInfo: UserInfo | null = null;
          let cachedRoleInfo: RoleInfo | null = null;

          if (userData && userData.roles) {
            const roleData = Array.isArray(userData.roles) ? userData.roles[0] : userData.roles;

            cachedUserInfo = {
              role_id: userData.role_id,
              tenant_id: userData.tenant_id,
              role: {
                id: roleData.id,
                name: roleData.name,
                global: roleData.global,
                tenant_id: roleData.tenant_id
              }
            };

            cachedRoleInfo = {
              id: roleData.id,
              name: roleData.name,
              global: roleData.global,
              tenant_id: roleData.tenant_id
            };
          }

          // If user has a role_id, use the legacy system
          if (userData?.role_id) {
            // Special handling for Superadmin (role_id 42)
            if (userData.role_id === 42) {
              return {
                permissions: new Set([
                  'system.override',
                  'manage_settings',
                  'settings.admin',
                  'user.manage_all',
                  'reporting.admin',
                  'manage_contracts',
                  'view_contracts',
                  'view_projects',
                  'project.view_all',
                  'project.view_team',
                  'manage_projects',
                  'edit_projects',
                  'view_commercial_pipeline',
                  'view_residential_pipeline',
                  'view_onboarding_pipeline',
                  'manage_users',
                  'tenant.manage'
                ]),
                userInfo: cachedUserInfo,
                roleInfo: cachedRoleInfo
              };
            }

            // Get permissions for this role
            // Use the role's tenant_id if available, otherwise use current tenant
            const roleData = Array.isArray(userData.roles) ? userData.roles[0] : userData.roles;

            // For global roles, we need to query permissions from all tenants they can access
            // For now, try both the role's original tenant (if any) and current tenant
            const isGlobalRole = roleData?.global === true;
            const roleTenantId = roleData?.tenant_id;
            const effectivePermissionTenantId = roleTenantId || currentTenant.tenant_id;

            // Build the query - for global roles, we might need to check multiple tenants
            let query = supabase
              .from('role_permissions')
              .select(`
                permission_id,
                tenant_id,
                permissions!inner (
                  name
                )
              `)
              .eq('role_id', userData.role_id);

            // If it's a global role, don't filter by tenant_id - get all permissions
            // Otherwise, filter by the specific tenant
            if (!isGlobalRole) {
              query = query.eq('tenant_id', effectivePermissionTenantId);
            }

            const { data: permissionData, error: permissionError } = await query;

            if (permissionError) {
              throw permissionError;
            }

            // Extract permission names into a Set for O(1) lookup
            const permissionSet = new Set<string>();

            if (permissionData) {
              permissionData.forEach((item: any) => {
                if (item.permissions?.name) {
                  permissionSet.add(item.permissions.name);
                }
              });
            }

            return {
              permissions: permissionSet,
              userInfo: cachedUserInfo,
              roleInfo: cachedRoleInfo
            };
          } else {
            // Use the new user_roles system
            const { data: userRolesData, error: userRolesError } = await supabase
              .from('user_roles')
              .select(`
                role_id,
                roles!inner (
                  id,
                  name,
                  global,
                  tenant_id
                )
              `)
              .eq('user_id', user.id)
              .eq('tenant_id', currentTenant.tenant_id);

            if (userRolesError) {
              throw userRolesError;
            }

            if (!userRolesData || userRolesData.length === 0) {
              return {
                permissions: new Set<string>(),
                userInfo: null,
                roleInfo: null
              };
            }

            // Set user and role info from user_roles data
            const firstRole = Array.isArray(userRolesData[0].roles) ? userRolesData[0].roles[0] : userRolesData[0].roles;

            cachedRoleInfo = {
              id: firstRole.id,
              name: firstRole.name,
              global: firstRole.global,
              tenant_id: firstRole.tenant_id
            };

            cachedUserInfo = {
              role_id: userRolesData[0].role_id,
              tenant_id: currentTenant.tenant_id,
              role: cachedRoleInfo
            };

            // Check if any role is Superadmin (role_id 42 or name 'Superadmin')
            const hasSuperadminRole = userRolesData.some(roleData => {
              const role = Array.isArray(roleData.roles) ? roleData.roles[0] : roleData.roles;
              return roleData.role_id === 42 || role?.name === 'Superadmin';
            });


            if (hasSuperadminRole) {
              // Grant all superadmin permissions just like the legacy system
              return {
                permissions: new Set([
                  'system.override',
                  'manage_settings',
                  'settings.admin',
                  'user.manage_all',
                  'reporting.admin',
                  'manage_contracts',
                  'view_contracts',
                  'view_projects',
                  'project.view_all',
                  'project.view_team',
                  'manage_projects',
                  'edit_projects',
                  'view_commercial_pipeline',
                  'view_residential_pipeline',
                  'view_onboarding_pipeline',
                  'manage_users',
                  'tenant.manage'
                ]),
                userInfo: cachedUserInfo,
                roleInfo: cachedRoleInfo
              };
            }
            
            // Get permissions for all user roles
            const roleIds = userRolesData.map(ur => ur.role_id);
            
            // For each role, we need to check if it's global and get permissions from the appropriate tenant
            // We'll need to fetch role details first to determine which are global
            const { data: rolesData, error: rolesError } = await supabase
              .from('roles')
              .select('id, global, tenant_id')
              .in('id', roleIds);
            
            if (rolesError) {
              throw rolesError;
            }
            
            // Create a map of role_id to effective tenant_id
            const roleToTenantMap = new Map();
            rolesData?.forEach(role => {
              // Use the role's tenant_id if available, otherwise use current tenant
              const effectiveTenantId = role.tenant_id || currentTenant.tenant_id;
              roleToTenantMap.set(role.id, effectiveTenantId);
            });
            
            // For simplicity, if any role is global, we'll query permissions from both tenants
            // and merge the results
            const tenantIds = Array.from(new Set(roleToTenantMap.values()));
            
            const { data: permissionData, error: permissionError } = await supabase
              .from('role_permissions')
              .select(`
                permission_id,
                role_id,
                tenant_id,
                permissions!inner (
                  name
                )
              `)
              .in('role_id', roleIds)
              .in('tenant_id', tenantIds);
            
            // Filter permissions to only include those that match the effective tenant for each role
            const filteredPermissionData = permissionData?.filter(item => {
              const effectiveTenantId = roleToTenantMap.get(item.role_id);
              return item.tenant_id === effectiveTenantId;
            });
            
            if (permissionError) {
              throw permissionError;
            }
            
            // Extract permission names into a Set for O(1) lookup
            const permissionSet = new Set<string>();
            
            // Use filtered permission data for user_roles system
            const dataToProcess = filteredPermissionData || permissionData;
            
            if (dataToProcess) {
              dataToProcess.forEach((item: any) => {
                if (item.permissions?.name) {
                  permissionSet.add(item.permissions.name);
                }
              });
            }

            return {
              permissions: permissionSet,
              userInfo: cachedUserInfo,
              roleInfo: cachedRoleInfo
            };
          }
        });
      });

      if (!signal.aborted) {
        setPermissions(result.permissions);
        setUserInfo(result.userInfo);
        setRoleInfo(result.roleInfo);
      }

    } catch (error: any) {
      if (!signal.aborted) {
        // Handle specific error types
        if (error?.status === 522) {
          setError('Connection timeout - please try again');
        } else if (error?.code === 'PGRST116') {
          setError('User not found');
        } else {
          setError('Failed to load user permissions');
        }
        
        // Keep empty permissions on error for security
        setPermissions(new Set());
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    // Only fetch permissions when auth and tenant are loaded AND we have a current tenant
    if (!authLoading && !tenantLoading && user && currentTenant && cacheKey) {
      fetchUserPermissions();
    } else if (!user) {
      // Clear permissions when user is not authenticated
      setPermissions(new Set());
      setLoading(false);
      setError(null);
    } else if (!authLoading && !tenantLoading && user && !currentTenant) {
      // Auth and tenant loading are done, but no current tenant 
      // Give it a moment for the currentTenant state to update, then clear if still null
      const timeoutId = setTimeout(() => {
        // Double-check after a brief delay to handle React state update timing
        if (!currentTenant && !tenantLoading) {
          setPermissions(new Set());
          setLoading(false);
          setError('No tenant access');
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    } else {
      // IMPORTANT: Keep loading=true while waiting for conditions to be met
      if (!loading) {
        setLoading(true);
      }
    }

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      isFetchingRef.current = false;
    };
  }, [user?.id, currentTenant?.tenant_id, authLoading, tenantLoading, cacheKey]);

  // Memoized permission checking functions for O(1) performance
  const hasPermission = useMemo(() => {
    return (permission: string): boolean => {
      return permissions.has(permission);
    };
  }, [permissions]);

  const hasAnyPermission = useMemo(() => {
    return (permissionList: string[]): boolean => {
      return permissionList.some((permission) => permissions.has(permission));
    };
  }, [permissions]);

  const hasAllPermissions = useMemo(() => {
    return (permissionList: string[]): boolean => {
      return permissionList.every((permission) => permissions.has(permission));
    };
  }, [permissions]);

  const refetch = async () => {
    // Clear cache for this user
    if (cacheKey) {
      permissionCache.delete(cacheKey);
    }
    await fetchUserPermissions();
  };

  const contextValue: PermissionContextType = useMemo(() => ({
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    userInfo,
    roleInfo,
    loading,
    error,
    refetch,
  }), [permissions, hasPermission, hasAnyPermission, hasAllPermissions, userInfo, roleInfo, loading, error, refetch]);

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
}

// Convenience hooks for common permission patterns
export function useHasPermission(permission: string) {
  const { hasPermission, loading } = usePermissions();
  return { hasPermission: hasPermission(permission), loading };
}

export function useHasAnyPermission(permissions: string[]) {
  const { hasAnyPermission, loading } = usePermissions();
  return { hasPermission: hasAnyPermission(permissions), loading };
}

export function useHasAllPermissions(permissions: string[]) {
  const { hasAllPermissions, loading } = usePermissions();
  return { hasPermission: hasAllPermissions(permissions), loading };
}
